/**
 * Federation: this server's identity, the servers it talks to, and the pairing
 * that gets a new one from a domain name to a trusted key.
 *
 * The whole page is arranged around one idea. relaii does not have a directory
 * or a certificate authority; the only thing standing between a peering and an
 * impostor is an administrator reading a fingerprint to another administrator
 * over a channel neither server controls. So the fingerprint is the largest
 * element on an approval card, the instruction to compare it out of band sits
 * next to it rather than in documentation, and nothing on this page approves
 * anything on the reader's behalf.
 */

import { useMemo, useRef, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'

import { useAuth } from '@/api/auth'
import { api } from '@/api/client'
import {
  queryKeys,
  useApprovePeer,
  useBootstrapPeer,
  useConnectPeer,
  useDeletePeer,
  useFederationIdentity,
  usePeers,
  useRevokePeer,
} from '@/api/queries'
import type { FederationIdentity, Peer, PeerBootstrap, PeerStatus } from '@/api/types'
import {
  Badge,
  Button,
  EmptyState,
  Modal,
  Spinner,
  TextField,
  useToast,
} from '@/components/ui'
import { formatRelative } from '@/lib/format'

import {
  AdminPage,
  Card,
  ErrorNotice,
  Fact,
  Facts,
  Notice,
  cx,
  describeError,
  isFederationDisabled,
  readField,
  readNumber,
  readText,
  useCopy,
} from './AdminLayout'
import federation from './FederationPage.module.css'
import styles from './admin.module.css'

// ── Fingerprints ────────────────────────────────────────────────────────────

/**
 * Groups of four, whatever separator the server used. Two people comparing a
 * key over the phone lose their place in a 32-character run; they do not lose
 * it in eight short groups.
 */
function fingerprintGroups(value: string): string[] {
  const hex = value.replace(/[^0-9a-fA-F]/gu, '').toLowerCase()
  const groups: string[] = []

  for (let index = 0; index < hex.length; index += 4) {
    groups.push(hex.slice(index, index + 4))
  }

  return groups
}

function canonicalFingerprint(value: string): string {
  return fingerprintGroups(value).join(' ')
}

function Fingerprint({ value, large = false }: { value: string; large?: boolean }): ReactElement {
  const groups = fingerprintGroups(value)

  if (groups.length === 0) {
    return <span className={styles.muted}>No key has been recorded for this server yet.</span>
  }

  return (
    <span className={cx(federation.fingerprint, large && federation.fingerprintLarge)}>
      {/*
        The groups exist for the eye. A screen reader gets the whole value once,
        rather than eight fragments it would announce as separate words.
      */}
      <span className="sr-only">{`Fingerprint ${groups.join(' ')}`}</span>
      {groups.map((group, index) => (
        <span
          aria-hidden="true"
          className={federation.fingerprintGroup}
          key={`${group}-${String(index)}`}
        >
          {group}
        </span>
      ))}
    </span>
  )
}

// ── Peer vocabulary ─────────────────────────────────────────────────────────

const STATUS_LABEL: Record<PeerStatus, string> = {
  pending: 'Waiting for approval',
  verifying: 'Verifying',
  active: 'Connected',
  revoked: 'Revoked',
  error: 'Not answering',
}

const STATUS_TONE: Record<PeerStatus, 'info' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  pending: 'warning',
  verifying: 'info',
  active: 'success',
  revoked: 'neutral',
  error: 'danger',
}

function peerName(peer: Peer): string {
  return peer.display_name ?? peer.server_id
}

function sharedChannelCount(peer: Peer): number | null {
  return readNumber(readField(peer, 'shared_channel_count'))
}

function lastSyncedAt(peer: Peer): string | null {
  return readText(readField(peer, 'last_synced_at'))
}

function lastError(peer: Peer): string | null {
  return readText(readField(peer, 'last_error'))
}

// ── The page ────────────────────────────────────────────────────────────────

export function FederationPage(): ReactElement {
  const auth = useAuth()
  const identity = useFederationIdentity()
  const peers = usePeers()

  const isOwner = auth.user?.is_owner === true

  const grouped = useMemo(() => {
    const all = peers.data ?? []
    return {
      awaiting: all.filter((peer) => peer.status === 'pending' || peer.status === 'verifying'),
      connected: all.filter((peer) => peer.status === 'active'),
      inactive: all.filter((peer) => peer.status === 'revoked' || peer.status === 'error'),
    }
  }, [peers.data])

  if (isFederationDisabled(identity.error) || isFederationDisabled(peers.error)) {
    return (
      <AdminPage title="Federation">
        <Card title="Federation is switched off">
          <div className={styles.stack}>
            <p>
              This server refuses every federated request, and no peering can be started or
              answered while that is so.
            </p>
            <p>
              <Link to="/admin">Turn federation on from the Server page</Link> to pair with another
              relaii server.
            </p>
          </div>
        </Card>
      </AdminPage>
    )
  }

  return (
    <AdminPage
      title="Federation"
      lead="Channels can be shared with other relaii servers. A pairing is only as trustworthy as the fingerprint check that established it."
    >
      <IdentityCard query={identity} isOwner={isOwner} />

      {peers.isError ? (
        <Card>
          <ErrorNotice error={peers.error} title="The list of servers could not be loaded">
            <Button onClick={() => void peers.refetch()}>Try again</Button>
          </ErrorNotice>
        </Card>
      ) : peers.isPending ? (
        <Card>
          <Spinner size="lg" label="Loading connected servers" />
        </Card>
      ) : (
        <>
          {grouped.awaiting.length > 0 ? (
            <Card
              title="Waiting for you to vouch for a key"
              lead="Approving one of these tells this server that the key below really belongs to that domain. Nothing else checks it."
            >
              <ul className={federation.peers}>
                {grouped.awaiting.map((peer) => (
                  <li key={peer.id}>
                    <PeerCard peer={peer} pending />
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <Card title="Connected servers">
            {grouped.connected.length === 0 ? (
              <EmptyState
                title="No servers are connected"
                description="Pair with another relaii server below. Both administrators have to act: one generates a connection key, the other spends it."
              />
            ) : (
              <ul className={federation.peers}>
                {grouped.connected.map((peer) => (
                  <li key={peer.id}>
                    <PeerCard peer={peer} />
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {grouped.inactive.length > 0 ? (
            <Card
              title="Revoked and failing"
              lead="Kept so that the history of the peering is visible. Removing one forgets it entirely."
            >
              <ul className={federation.peers}>
                {grouped.inactive.map((peer) => (
                  <li key={peer.id}>
                    <PeerCard peer={peer} />
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </>
      )}

      <div className={federation.pairing}>
        <AddServer />
        <ConnectToServer />
      </div>

      {isOwner ? <RotateIdentityKey identity={identity.data} /> : null}
    </AdminPage>
  )
}

// ── This server ─────────────────────────────────────────────────────────────

function IdentityCard({
  query,
  isOwner,
}: {
  query: UseQueryResult<FederationIdentity>
  isOwner: boolean
}): ReactElement {
  const copy = useCopy()
  const identity = query.data

  return (
    <Card
      title="This server"
      lead="What another administrator needs from you before they will trust anything this server says."
    >
      {query.isError ? (
        <ErrorNotice error={query.error} title="This server's identity could not be read">
          <Button onClick={() => void query.refetch()}>Try again</Button>
        </ErrorNotice>
      ) : identity === undefined ? (
        <Spinner size="lg" label="Reading this server's identity" />
      ) : (
        <div className={styles.stack}>
          <div className={federation.fingerprintBlock}>
            <span className={federation.fingerprintLabel}>Public key fingerprint</span>
            <Fingerprint value={identity.fingerprint} large />
            <div className={styles.rowCentred}>
              <Button
                onClick={() =>
                  copy(canonicalFingerprint(identity.fingerprint), 'This server’s fingerprint')
                }
              >
                Copy fingerprint
              </Button>
            </div>
          </div>

          <Facts>
            <Fact term="Server address">
              <span className={styles.mono}>{identity.server_id}</span>
            </Fact>
            <Fact term="Base URL">
              <span className={styles.mono}>{identity.base_url}</span>
            </Fact>
            <Fact term="Federation protocol">
              {identity.federation_versions.length === 0
                ? 'Not advertised'
                : identity.federation_versions.map((version) => `v${String(version)}`).join(', ')}
            </Fact>
            <Fact term="Signing keys">
              {identity.keys.length === 0
                ? 'None'
                : identity.keys
                    .map((key) => `${key.id} (${key.algorithm})`)
                    .join(', ')}
            </Fact>
          </Facts>

          {isOwner ? null : (
            <p className={styles.small}>
              Only the owner of this server can rotate this key.
            </p>
          )}
        </div>
      )}
    </Card>
  )
}

// ── One peer ────────────────────────────────────────────────────────────────

function PeerCard({ peer, pending = false }: { peer: Peer; pending?: boolean }): ReactElement {
  const approve = useApprovePeer()
  const revokePeer = useRevokePeer()
  const removePeer = useDeletePeer()
  const toast = useToast()
  const copy = useCopy()

  const [confirming, setConfirming] = useState<'revoke' | 'remove' | null>(null)

  const shared = sharedChannelCount(peer)
  const synced = lastSyncedAt(peer)
  const failure = lastError(peer)

  const onApprove = (): void => {
    approve.mutate(peer.id, {
      onSuccess: () => {
        toast.success(`${peerName(peer)} is now connected.`)
      },
      onError: (error: unknown) => {
        toast.error(describeError(error, 'That server could not be approved.'))
      },
    })
  }

  const onRevoke = (): void => {
    revokePeer.mutate(peer.id, {
      onSuccess: () => {
        setConfirming(null)
        toast.success(`${peerName(peer)} has been revoked.`)
      },
      onError: (error: unknown) => {
        toast.error(describeError(error, 'That server could not be revoked.'))
      },
    })
  }

  const onRemove = (): void => {
    removePeer.mutate(peer.id, {
      onSuccess: () => {
        setConfirming(null)
        toast.success(`${peerName(peer)} has been removed.`)
      },
      onError: (error: unknown) => {
        toast.error(describeError(error, 'That server could not be removed.'))
      },
    })
  }

  return (
    <article className={cx(federation.peer, pending && federation.peerPending)}>
      <header className={federation.peerHead}>
        <div>
          <p className={federation.peerName}>{peerName(peer)}</p>
          <p className={federation.peerDomain}>{peer.server_id}</p>
        </div>
        <Badge tone={STATUS_TONE[peer.status]}>{STATUS_LABEL[peer.status]}</Badge>
      </header>

      {pending ? (
        <div className={federation.fingerprintBlock}>
          <span className={federation.fingerprintLabel}>Their public key fingerprint</span>
          <Fingerprint value={peer.fingerprint ?? ''} large />
          <p className={federation.compare}>
            Read this out to the administrator of <strong>{peer.server_id}</strong> over something
            neither server controls — a phone call, or a conversation you are already having
            elsewhere. Approve only if every group matches. A fingerprint that does not match means
            somebody else is answering for that domain.
          </p>
          {peer.fingerprint === null ? null : (
            <div className={styles.rowCentred}>
              <Button
                size="sm"
                onClick={() => copy(canonicalFingerprint(peer.fingerprint ?? ''), 'The fingerprint')}
              >
                Copy fingerprint
              </Button>
            </div>
          )}
        </div>
      ) : (
        <Facts>
          <Fact term="Fingerprint">
            <Fingerprint value={peer.fingerprint ?? ''} />
          </Fact>
        </Facts>
      )}

      <div className={federation.peerStats}>
        <div className={federation.stat}>
          <span className={federation.statLabel}>Protocol</span>
          <span className={federation.statValue}>
            {peer.federation_versions.length === 0
              ? 'Unknown'
              : peer.federation_versions.map((version) => `v${String(version)}`).join(', ')}
          </span>
        </div>
        <div className={federation.stat}>
          <span className={federation.statLabel}>Shared channels</span>
          <span className={federation.statValue}>{shared === null ? '—' : shared}</span>
        </div>
        <div className={federation.stat}>
          <span className={federation.statLabel}>Last sync</span>
          <span className={federation.statValue}>
            {synced === null ? 'Never' : formatRelative(synced)}
          </span>
        </div>
        <div className={federation.stat}>
          <span className={federation.statLabel}>Last heard from</span>
          <span className={federation.statValue}>
            {peer.last_seen_at === null ? 'Never' : formatRelative(peer.last_seen_at)}
          </span>
        </div>
        <div className={federation.stat}>
          <span className={federation.statLabel}>Direction</span>
          <span className={federation.statValue}>
            {peer.direction === 'outbound' ? 'We asked them' : 'They asked us'}
          </span>
        </div>
      </div>

      {failure === null ? null : (
        <Notice tone="danger" title="Last failure">
          <span className={styles.mono}>{failure}</span>
        </Notice>
      )}

      <div className={federation.peerActions}>
        {peer.status === 'pending' || peer.status === 'verifying' ? (
          <Button variant="primary" loading={approve.isPending} onClick={onApprove}>
            The fingerprint matches — approve
          </Button>
        ) : null}
        {peer.status === 'active' ? (
          <Button variant="danger" onClick={() => setConfirming('revoke')}>
            Revoke
          </Button>
        ) : null}
        <Button onClick={() => setConfirming('remove')}>Remove</Button>
      </div>

      <Modal
        open={confirming === 'revoke'}
        onClose={() => setConfirming(null)}
        title={`Revoke ${peer.server_id}?`}
        size="sm"
        footer={
          <>
            <Button onClick={() => setConfirming(null)}>Cancel</Button>
            <Button variant="danger" loading={revokePeer.isPending} onClick={onRevoke}>
              Revoke this server
            </Button>
          </>
        }
      >
        <div className={styles.stack}>
          <p>Revoking takes effect at once. From that moment:</p>
          <ul>
            <li>
              every channel shared with <strong>{peer.server_id}</strong> stops replicating in both
              directions;
            </li>
            <li>their people lose access to those channels and to their history there;</li>
            <li>anything still queued for them is dropped rather than delivered;</li>
            <li>
              their key is no longer trusted, so reconnecting means pairing again from the start —
              a new connection key and a fresh fingerprint check.
            </li>
          </ul>
          <p>Messages already delivered to them stay on their server. This does not recall them.</p>
        </div>
      </Modal>

      <Modal
        open={confirming === 'remove'}
        onClose={() => setConfirming(null)}
        title={`Remove ${peer.server_id}?`}
        size="sm"
        footer={
          <>
            <Button onClick={() => setConfirming(null)}>Cancel</Button>
            <Button variant="danger" loading={removePeer.isPending} onClick={onRemove}>
              Remove this server
            </Button>
          </>
        }
      >
        <p>
          The record of this peering is forgotten, including the key you approved. Pairing again
          later starts from scratch, with a new fingerprint to compare.
        </p>
      </Modal>
    </article>
  )
}

// ── Pairing, from this side ─────────────────────────────────────────────────

function AddServer(): ReactElement {
  const bootstrap = useBootstrapPeer()
  const toast = useToast()
  const copy = useCopy()

  const [domain, setDomain] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [issued, setIssued] = useState<PeerBootstrap | null>(null)

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()

    const serverId = domain.trim().toLowerCase()
    if (serverId === '') return

    const name = displayName.trim()

    bootstrap.mutate(
      name === '' ? { server_id: serverId } : { server_id: serverId, display_name: name },
      {
        onSuccess: (result) => {
          setIssued(result)
          setDomain('')
          setDisplayName('')
          toast.success('Connection key generated.')
        },
        onError: (error: unknown) => {
          toast.error(describeError(error, 'A connection key could not be generated.'))
        },
      },
    )
  }

  return (
    <Card
      title="Add a server"
      lead="You generate a key and read it out to their administrator. They spend it on their own server."
    >
      <div className={styles.stack}>
        <form className={styles.stack} onSubmit={submit} noValidate>
          <TextField
            label="Their domain"
            required
            value={domain}
            onChange={(event) => setDomain(event.target.value)}
            placeholder="chat.partner.no"
            hint="The address of their relaii server, without a scheme."
            autoComplete="off"
            spellCheck={false}
          />
          <TextField
            label="A name for them"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            hint="Optional. Only shown on this server."
            autoComplete="off"
          />
          <div className={styles.cellActions}>
            <Button type="submit" variant="primary" loading={bootstrap.isPending}>
              Generate a connection key
            </Button>
          </div>
        </form>

        {issued === null ? null : (
          <div className={styles.secret}>
            <div>
              <p className={styles.noticeTitle}>Give these three to their administrator</p>
              <p className={styles.small}>
                The key is shown once and cannot be read back. It expires{' '}
                <strong title={issued.expires_at}>{formatRelative(issued.expires_at)}</strong> —
                about thirty minutes from now. Generating another one invalidates this.
              </p>
            </div>

            <div className={styles.secretValue}>
              <span className={styles.secretText}>{issued.server_id}</span>
              <Button size="sm" onClick={() => copy(issued.server_id, 'This server’s domain')}>
                Copy
              </Button>
            </div>
            <div className={styles.secretValue}>
              <span className={styles.secretText}>{issued.base_url}</span>
              <Button size="sm" onClick={() => copy(issued.base_url, 'This server’s base URL')}>
                Copy
              </Button>
            </div>
            <div className={styles.secretValue}>
              <span className={styles.secretText}>{issued.bootstrap_key}</span>
              <Button
                size="sm"
                variant="primary"
                onClick={() => copy(issued.bootstrap_key, 'The connection key')}
              >
                Copy key
              </Button>
            </div>

            <div className={styles.cellActions}>
              <Button size="sm" onClick={() => setIssued(null)}>
                Hide
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

function ConnectToServer(): ReactElement {
  const connect = useConnectPeer()
  const toast = useToast()

  const [domain, setDomain] = useState('')
  const [key, setKey] = useState('')
  const [baseUrl, setBaseUrl] = useState('')

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()

    const serverId = domain.trim().toLowerCase()
    const bootstrapKey = key.trim()
    if (serverId === '' || bootstrapKey === '') return

    const url = baseUrl.trim()

    connect.mutate(
      {
        server_id: serverId,
        base_url: url === '' ? `https://${serverId}` : url,
        bootstrap_key: bootstrapKey,
      },
      {
        onSuccess: (peer) => {
          setDomain('')
          setKey('')
          setBaseUrl('')
          toast.success(
            peer.status === 'active'
              ? `${peerName(peer)} is connected.`
              : `${peerName(peer)} has been contacted. Compare the fingerprint before approving.`,
          )
        },
        onError: (error: unknown) => {
          toast.error(describeError(error, 'That server could not be reached.'))
        },
      },
    )
  }

  return (
    <Card
      title="Connect to a server"
      lead="Their administrator generated a key and gave it to you. This spends it."
    >
      <form className={styles.stack} onSubmit={submit} noValidate>
        <TextField
          label="Their domain"
          required
          value={domain}
          onChange={(event) => setDomain(event.target.value)}
          placeholder="chat.partner.no"
          autoComplete="off"
          spellCheck={false}
        />
        <TextField
          label="Connection key"
          required
          value={key}
          onChange={(event) => setKey(event.target.value)}
          hint="The one-time key their administrator read out to you. It expires quickly."
          autoComplete="off"
          spellCheck={false}
        />
        <TextField
          label="Their base URL"
          value={baseUrl}
          onChange={(event) => setBaseUrl(event.target.value)}
          placeholder={domain.trim() === '' ? 'https://chat.partner.no' : `https://${domain.trim()}`}
          hint="Optional. Only needed when their server is not served from its own domain."
          autoComplete="off"
          spellCheck={false}
        />
        <div className={styles.cellActions}>
          <Button type="submit" variant="primary" loading={connect.isPending}>
            Connect
          </Button>
        </div>
      </form>
    </Card>
  )
}

// ── Rotating this server's key ──────────────────────────────────────────────

function RotateIdentityKey({ identity }: { identity: FederationIdentity | undefined }): ReactElement {
  const queryClient = useQueryClient()
  const toast = useToast()

  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const confirmField = useRef<HTMLInputElement>(null)

  const rotate = useMutation({
    mutationFn: () => api.post<FederationIdentity>('/admin/federation/keys/rotate'),
    onSuccess: (next) => {
      queryClient.setQueryData<FederationIdentity>(queryKeys.admin.federation.identity(), next)
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.federation.peers() })
    },
  })

  const serverId = identity?.server_id ?? ''
  const close = (): void => {
    setOpen(false)
    setTyped('')
  }

  const confirm = (): void => {
    rotate.mutate(undefined, {
      onSuccess: () => {
        close()
        toast.success('A new identity key is in use. Read the new fingerprint out to every peer.')
      },
      onError: (error: unknown) => {
        toast.error(describeError(error, 'The identity key could not be rotated.'))
      },
    })
  }

  return (
    <Card
      danger
      title="Rotate this server's identity key"
      lead="Only the owner of this server can do this."
    >
      <div className={styles.stack}>
        <Notice tone="danger" title="Every peer has to verify you again">
          A new key means a new fingerprint. Until each partner administrator has compared the new
          one and re-approved this server, replication with them stops: queued events stay queued
          and shared channels go quiet. Rotate when the private key may have been exposed — not as
          routine maintenance.
        </Notice>

        <div className={styles.cellActions}>
          <Button variant="danger" onClick={() => setOpen(true)} disabled={identity === undefined}>
            Rotate identity key…
          </Button>
        </div>
      </div>

      <Modal
        open={open}
        onClose={close}
        title="Rotate the federation identity key"
        description="Every existing peering has to be verified again by hand."
        size="sm"
        closeOnOverlayClick={false}
        initialFocusRef={confirmField}
        footer={
          <>
            <Button onClick={close}>Cancel</Button>
            <Button
              variant="danger"
              loading={rotate.isPending}
              disabled={typed.trim() !== serverId || serverId === ''}
              onClick={confirm}
            >
              Rotate the key
            </Button>
          </>
        }
      >
        <div className={styles.stack}>
          <p>
            Plan for this: contact every partner administrator first, and have a way to reach them
            that does not run through relaii. Federation with each of them is down until they have
            re-verified.
          </p>
          <TextField
            ref={confirmField}
            label={`Type ${serverId} to confirm`}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </Modal>
    </Card>
  )
}
